import { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import {
  addOffer,
  updateOffer,
  checkItemPresent,
  fetchItems,
} from "store/actions/offerActions";
import ItemSelector from "./ItemSelector";
import ImageUploadInput from "components/shared/ImageUploadInput";

const OfferModal = ({ isOpen, onClose, editMode, data, restaurantId }) => {
  const dispatch = useDispatch();
  const { loading, allItems } = useSelector((state) => state.offer);

  const [form, setForm] = useState({
    title: "",
    price: "",
    image: null,
    selectedItems: [],
  });

  const [selection, setSelection] = useState({
    currentItem: null,
    currentVariant: null,
  });

  const [itemsLoading, setItemsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const updateForm = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const resetFields = useCallback(() => {
    setForm({
      title: "",
      price: "",
      image: null,
      variant: "",
      selectedItems: [],
    });
    setSelection({
      currentItem: null,
      currentVariant: null,
    });
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.includes("image")) {
      toast.error("Only image files are allowed");
      return;
    }
    updateForm("image", file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { title, price, image, selectedItems } = form;
    if (!title || !price || !image) {
      return toast.error("All fields are required");
    }
    if (!/^\d+(\.\d{1,2})?$/.test(price)) {
      return toast.error("Price must be a numeric value");
    }

    const payload = {
      title,
      price,
      image: image || data?.image,
      items: selectedItems,
    };

    editMode
      ? await dispatch(
          updateOffer(data?.id, payload, () => {
            onClose();
            resetFields();
          })
        )
      : await dispatch(
          addOffer(restaurantId, payload, () => {
            onClose();
            resetFields();
          })
        );
  };

  const addItemToOffer = (currentVariant) => {
    const { currentItem } = selection;
    if (!currentItem || !currentVariant) return;

    setForm((prev) => {
      const existingIndex = prev.selectedItems.findIndex(
        (item) =>
          item.id === currentItem.value && item.variant === currentVariant.value
      );

      let updatedItems = [...prev.selectedItems];

      if (existingIndex !== -1) {
        const existingItem = updatedItems[existingIndex];

        updatedItems[existingIndex] = {
          ...existingItem,
          quantity: existingItem.quantity + 1,
        };
      } else {
        updatedItems.push({
          id: currentItem.value,
          itemName: currentItem.label,
          image: currentItem.image,
          variant: currentVariant.value,
          quantity: 1,
        });
      }

      return { ...prev, selectedItems: updatedItems };
    });

    setSelection({
      currentItem: null,
      currentVariant: null,
    });
  };

  const removeSelectedItem = (id, variant) => {
    setForm((prev) => ({
      ...prev,
      selectedItems: prev.selectedItems.reduce((acc, item) => {
        if (item.id === id && item.variant === variant && item.quantity > 1) {
          acc.push({ ...item, quantity: item.quantity - 1 });
        } else if (!(item.id === id && item.variant === variant)) {
          acc.push(item);
        }
        return acc;
      }, []),
    }));
  };

  useEffect(() => {
    if (!restaurantId) return;
    const loadItems = async () => {
      setItemsLoading(true);
      try {
        const canFetch = await dispatch(checkItemPresent(restaurantId));
        if (canFetch) await dispatch(fetchItems(restaurantId));
        else setErrorMsg("No items found.");
      } finally {
        setItemsLoading(false);
      }
    };
    loadItems();
  }, [dispatch, restaurantId]);

  useEffect(() => {
    if (editMode && data) {
      setForm({
        title: data?.title || "",
        price: data?.price || "",
        image: data?.image || null,
        variant: data?.variant || "",
        selectedItems: data?.items || [],
      });
    } else {
      resetFields();
    }
  }, [editMode, data, resetFields]);

  return (
    <Modal isOpen={isOpen} toggle={onClose}>
      <ModalHeader toggle={onClose}>
        {editMode ? "Edit" : "Add"} Offer
      </ModalHeader>
      <Form onSubmit={handleSubmit}>
        <ModalBody>
          <FormGroup>
            <Label>Offer Name</Label>
            <Input
              value={form.title}
              maxLength={70}
              onChange={(e) => updateForm("title", e.target.value)}
              placeholder="Enter offer title"
              required
            />
          </FormGroup>
          <ItemSelector
            allItems={allItems}
            itemsLoading={itemsLoading}
            currentItem={selection.currentItem}
            currentVariant={selection.currentVariant}
            setCurrentItem={(val) =>
              setSelection((prev) => ({ ...prev, currentItem: val }))
            }
            setCurrentVariant={(val) => {
              setSelection((prev) => ({ ...prev, currentVariant: val }));
              addItemToOffer(val);
            }}
            itemsError={errorMsg}
          />
          {form.selectedItems.length > 0 && (
            <>
              <Label>Added Items</Label>
              <Row>
                {form.selectedItems.map((item) => (
                  <Col
                    key={`${item.id}-${item.variant}`}
                    xs={6}
                    className="mb-3"
                  >
                    <div className="border p-2 text-center rounded">
                      <img
                        src={item.image}
                        style={{
                          objectFit: "cover",
                          height: "100px",
                        }}
                        className="w-100 rounded"
                        alt={item?.itemName}
                      />
                      <div>{item.itemName}</div>
                      <div className="text-muted small">
                        ×{item.quantity} {item.variant}
                      </div>
                      <Button
                        size="sm"
                        color="danger"
                        className="mt-2"
                        onClick={() =>
                          removeSelectedItem(item.id, item.variant)
                        }
                      >
                        Remove
                      </Button>
                    </div>
                  </Col>
                ))}
              </Row>
            </>
          )}

          <FormGroup className="mt-3">
            <Label>Price</Label>
            <Input
              type="number"
              value={form.price}
              onChange={(e) => updateForm("price", e.target.value)}
              placeholder="Enter price"
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>Image</Label>
            <ImageUploadInput
              image={form.image}
              onImageChange={handleImageChange}
              onRemoveImage={() => setForm({ ...form, image: null })}
              isLoading={loading}
              fullPreview
              height="200px"
            />
          </FormGroup>
        </ModalBody>

        <ModalFooter>
          <Button color="primary" type="submit" disabled={loading}>
            {loading ? <Spinner size="sm" /> : "Save"}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default OfferModal;
